/**
 * RecipeCard Component
 *
 * Displays a single recipe card with key information including title,
 * description, preparation time, and cooking time.
 */

import { useTranslation } from 'react-i18next';
import type { Recipe } from '../../types/recipe';
import './recipes.css';

interface RecipeCardProps {
  recipe: Recipe;
}

export function RecipeCard({ recipe }: RecipeCardProps) {
  const { t } = useTranslation('recipes');
  const totalTime = (recipe.prep_time || 0) + (recipe.cook_time || 0);

  // Build accessible description
  const buildAriaLabel = () => {
    const parts = [recipe.title];

    if (recipe.description) {
      parts.push(recipe.description);
    }

    if (totalTime > 0) {
      parts.push(t('minutesTotalTime', { time: totalTime }));
    }

    if (recipe.servings) {
      parts.push(t('servings', { count: recipe.servings }));
    }

    if (recipe.ingredients && recipe.ingredients.length > 0) {
      parts.push(t('ingredient', { count: recipe.ingredients.length }));
    }

    return parts.join(', ');
  };

  return (
    <a
      href={`/recipes/${recipe.id}`}
      className="recipe-card"
      aria-label={buildAriaLabel()}
    >
      <div className="recipe-card-content">
        <h3 className="recipe-card-title">{recipe.title}</h3>
        {recipe.description && (
          <p className="recipe-card-description">{recipe.description}</p>
        )}

        <div className="recipe-card-meta" aria-label="Recipe metadata">
          {totalTime > 0 && (
            <div className="recipe-meta-item">
              <span className="meta-icon" aria-hidden="true">⏱</span>
              <span>{t('min', { time: totalTime })}</span>
            </div>
          )}
          {recipe.servings && (
            <div className="recipe-meta-item">
              <span className="meta-icon" aria-hidden="true">🍽</span>
              <span>{t('servings', { count: recipe.servings })}</span>
            </div>
          )}
        </div>

        {recipe.ingredients && recipe.ingredients.length > 0 && (
          <div className="recipe-card-ingredients">
            <span className="ingredients-label">
              {t('ingredient', { count: recipe.ingredients.length })}
            </span>
          </div>
        )}

        {recipe.nutrients_per_serving && (
          <div className="recipe-card-nutrients" aria-label={t('nutrientsPerServing')}>
            <span className="nutrients-badge">
              {[
                recipe.nutrients_per_serving.calories != null && `${recipe.nutrients_per_serving.calories} ${t('kcal')}`,
                recipe.nutrients_per_serving.proteins != null && `Б ${recipe.nutrients_per_serving.proteins}${t('grams')}`,
                recipe.nutrients_per_serving.fats != null && `Ж ${recipe.nutrients_per_serving.fats}${t('grams')}`,
                recipe.nutrients_per_serving.carbohydrates != null && `У ${recipe.nutrients_per_serving.carbohydrates}${t('grams')}`,
              ].filter(Boolean).join(' / ')}
            </span>
          </div>
        )}
      </div>
    </a>
  );
}

export default RecipeCard;
